"use client"

import { memo, useEffect, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"

type Props = {
  image: string
  ready?: boolean
}

const DEFAULT_BG = "/image/app/logo.jpg"

function PlayerBackground({
  image,
  ready = true,
}: Props) {
  const [visibleImage, setVisibleImage] = useState(
    image || DEFAULT_BG,
  )

  const [previousImage, setPreviousImage] = useState<string | null>(
    null,
  )

  useEffect(() => {
    const nextImage = image || DEFAULT_BG

    if (nextImage === visibleImage) return

    let cancelled = false
    const img = new Image()

    img.decoding = "async"

    img.onload = () => {
      if (cancelled) return

      setPreviousImage(visibleImage)
      setVisibleImage(nextImage)
    }

    img.onerror = () => {
      if (cancelled) return

      if (visibleImage !== DEFAULT_BG) {
        setPreviousImage(visibleImage)
        setVisibleImage(DEFAULT_BG)
      }
    }

    img.src = nextImage

    if (img.complete) {
      img.onload?.(new Event("load") as Event)
    }

    return () => {
      cancelled = true
      img.onload = null
      img.onerror = null
    }
  }, [image, visibleImage])

  return (
    <div
      aria-hidden="true"
      className="
        pointer-events-none
        absolute
        inset-0
        z-0
        isolate
        overflow-hidden
        rounded-[inherit]
        bg-black
      "
    >
      {/* =====================================================
          IMAGEN ACTUAL
      ===================================================== */}

      <div className="absolute inset-0 overflow-hidden">
        <div
          className="
            absolute
            -inset-[12%]
            bg-cover
            bg-center
            bg-no-repeat
          "
          style={{
            backgroundImage: `url("${visibleImage}")`,
            filter: "blur(32px)",
            transform: "translate3d(0,0,0) scale(1.12)",
            WebkitTransform:
              "translate3d(0,0,0) scale(1.12)",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            willChange: "transform",
          }}
        />
      </div>

      {/* =====================================================
          TRANSICIÓN
      ===================================================== */}

      <AnimatePresence initial={false}>
        {previousImage && ready && (
          <motion.div
            key={previousImage}
            initial={{ opacity: 0.75 }}
            animate={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.45,
              ease: "easeOut",
            }}
            onAnimationComplete={() => {
              setPreviousImage(null)
            }}
            className="
              absolute
              inset-0
              overflow-hidden
            "
          >
            <div
              className="
                absolute
                -inset-[12%]
                bg-cover
                bg-center
                bg-no-repeat
              "
              style={{
                backgroundImage: `url("${previousImage}")`,
                filter: "blur(32px)",
                transform:
                  "translate3d(0,0,0) scale(1.12)",
                WebkitTransform:
                  "translate3d(0,0,0) scale(1.12)",
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* =====================================================
          PROFUNDIDAD / CONTRASTE
      ===================================================== */}

      <div
        className="
          absolute
          inset-0
          bg-black/30
        "
      />

      <div
        className="
          absolute
          inset-0
          bg-[radial-gradient(
            circle_at_center,
            rgba(70,70,70,.12),
            rgba(0,0,0,.68)
          )]
        "
      />

      {/* =====================================================
          VIÑETA INFERIOR
      ===================================================== */}

      <div
        className="
          absolute
          inset-x-0
          bottom-0
          h-2/3
          bg-gradient-to-t
          from-black/45
          via-black/10
          to-transparent
        "
      />
    </div>
  )
}

export default memo(PlayerBackground)